import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Container,
	Grid,
	MenuItem,
	Skeleton,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { RootState } from 'store';
import { Layout } from 'features/common/components/Layout';
import { isAdminOrSuperAdmin } from 'features/auth/helpers';
import { countriesWithAgencyConfig } from 'features/auth/constants';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { AgingTable } from 'features/receivables/components/AgingTable';
import { AgingDrilldown, DrilldownTarget } from 'features/receivables/components/AgingDrilldown';
import { carteraInfoTooltip, fechaCorteTooltip, SEGMENTO_LABEL, SEGMENTOS } from 'features/receivables/constants';
import { agingToCsv, descargarCsv, hoyISO, usd } from 'features/receivables/helpers';
import { useAging } from 'features/receivables/hooks/useReceivables';
import { Segmento } from 'features/receivables/types';

/** Una cifra grande arriba de todo, con su bajada. */
function Kpi({
	label,
	value,
	color,
	hint,
}: {
	label: string;
	value: string;
	color?: string;
	hint?: string;
}): JSX.Element {
	return (
		<Card variant="outlined" sx={{ height: '100%' }}>
			<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
				<Typography variant="caption" color="text.secondary">
					{label}
				</Typography>
				<Typography variant="h6" fontWeight={700} color={color ?? 'text.primary'}>
					{value}
				</Typography>
				{hint && (
					<Typography variant="caption" color="text.disabled">
						{hint}
					</Typography>
				)}
			</CardContent>
		</Card>
	);
}

/** /cobros/cartera — salud de la cartera de clientes con cuenta corriente. */
export function ReceivablesRoute(): JSX.Element {
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);

	const [segments, setSegments] = useState<Segmento[]>([...SEGMENTOS]);
	const [agencyId, setAgencyId] = useState<number | undefined>(undefined);
	const [asOf, setAsOf] = useState<string>(hoyISO());
	const [target, setTarget] = useState<DrilldownTarget | null>(null);

	const { data, loading, error, refetch } = useAging({ segments, agencyId, asOf });

	if (!isAdminOrSuperAdmin(user)) {
		return <Redirect to="/" />;
	}

	const t = data?.totales;

	return (
		<Layout>
			<Container maxWidth={false} sx={{ py: 3 }}>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
					<Box>
						<Box display="flex" alignItems="center" gap={0.75}>
							<Typography variant="h5" fontWeight={700} color="primary">
								Salud de cartera
							</Typography>
							<SectionInfoTooltip content={carteraInfoTooltip} />
						</Box>
						<Typography variant="body2" color="text.secondary">
							Cuánto nos deben los clientes con cuenta corriente, hace cuánto, y qué falta resolver de nuestro lado.
							Todo en dólares.
						</Typography>
					</Box>

					<Stack direction="row" spacing={1} alignItems="center">
						<Tooltip title="Volver a calcular con los datos de ahora">
							<span>
								<Button
									size="small"
									startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
									onClick={() => refetch({ refresh: true })}
									disabled={loading}
								>
									Actualizar
								</Button>
							</span>
						</Tooltip>
						<Button
							size="small"
							startIcon={<DownloadIcon />}
							disabled={!data}
							onClick={() => data && descargarCsv(agingToCsv(data), `cartera-${asOf}.csv`)}
						>
							Exportar
						</Button>
					</Stack>
				</Box>

				{/* Filtros */}
				<Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
					<ToggleButtonGroup
						size="small"
						value={segments}
						onChange={(_, v: Segmento[]) => v.length > 0 && setSegments(v)}
					>
						{SEGMENTOS.map((s) => (
							<ToggleButton key={s} value={s} sx={{ textTransform: 'none', px: 1.5 }}>
								{SEGMENTO_LABEL[s]}
							</ToggleButton>
						))}
					</ToggleButtonGroup>

					<TextField
						select
						size="small"
						label="Agencia"
						value={agencyId ?? ''}
						onChange={(e) => setAgencyId(e.target.value === '' ? undefined : Number(e.target.value))}
						sx={{ minWidth: 180 }}
					>
						<MenuItem value="">Todas</MenuItem>
						{countriesWithAgencyConfig.map((a) => (
							<MenuItem key={a.agencyId} value={a.agencyId}>
								{a.countryName}
							</MenuItem>
						))}
					</TextField>

					<Box display="flex" alignItems="center" gap={0.5}>
						<TextField
							type="date"
							size="small"
							label="Fecha de corte"
							value={asOf}
							onChange={(e) => setAsOf(e.target.value || hoyISO())}
							InputLabelProps={{ shrink: true }}
							sx={{ width: 175 }}
						/>
						<SectionInfoTooltip content={fechaCorteTooltip} />
					</Box>

					{asOf !== hoyISO() && (
						<Chip size="small" color="warning" label="Mirando una fecha pasada" onDelete={() => setAsOf(hoyISO())} />
					)}
				</Stack>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => refetch()}>Reintentar</Button>}>
						{error}
					</Alert>
				)}

				{/* KPIs */}
				<Grid container spacing={1.5} mb={2}>
					{[
						{ label: 'Vencido', value: t?.vencidoTotal, color: 'error.main', hint: 'Se reclama hoy' },
						{ label: 'Por vencer', value: t?.porVencerTotal, hint: 'Facturado, todavía en plazo' },
						{
							label: 'Pendiente de facturar',
							value: t?.pendienteFacturar,
							color: 'info.main',
							hint: 'Tarea nuestra',
						},
						{
							label: 'Saldo a favor',
							value: t?.saldoAFavor,
							color: 'success.main',
							hint: t ? `${t.clientesConSaldoAFavor} clientes` : undefined,
						},
						{
							label: 'Diferencia a revisar',
							value: t?.diferencia,
							color: 'warning.dark',
							hint: 'Recibos sin imputar o saldos sin documentar',
						},
					].map((k) => (
						<Grid item xs={6} md={2.4} key={k.label}>
							{loading && !data ? (
								<Skeleton variant="rectangular" height={78} />
							) : (
								<Kpi label={k.label} value={k.value === undefined ? '—' : usd(k.value)} color={k.color} hint={k.hint} />
							)}
						</Grid>
					))}
				</Grid>

				{loading && !data ? (
					<Skeleton variant="rectangular" height={420} />
				) : data ? (
					<>
						<AgingTable data={data} onDrilldown={setTarget} />
						<Typography variant="caption" color="text.disabled" display="block" mt={1}>
							{data.totales.clientes} clientes con saldo · {data.totales.ordenesSinDefinir} órdenes con más de un
							cliente facturable · Click en cualquier importe para ver el detalle.
						</Typography>
					</>
				) : null}

				<AgingDrilldown target={target} onClose={() => setTarget(null)} segments={segments} asOf={asOf} />
			</Container>
		</Layout>
	);
}
